/* Cautare predictiva in header, peste API-ul nativ Shopify (`/search/suggest.json`).
   Formularul ramane un <form> real catre /search, deci fara JavaScript cautarea tot merge;
   sugestiile sunt doar un strat peste. */
(() => {
  const DEBOUNCE = 220;
  const MIN_CHARS = 2;

  /* `/search/suggest.json` da pretul ca TEXT, in lei ("89.00") — nu in bani, spre deosebire
     de `/cart.js` si de restul API-urilor de cos. Impartirea la 100 arata "0,89 lei". */
  function money(value) {
    const n = Number(String(value).replace(',', '.'));
    if (!isFinite(n)) return '';
    const [lei, bani] = n.toFixed(2).split('.');
    return lei.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + bani + ' lei';
  }

  /* Familiile au preturi diferite pe nuante (PLA Basic: 89–99), deci un pret unic ar minti. */
  function priceLabel(p) {
    const min = p.price_min != null ? p.price_min : p.price;
    if (min == null) return '';
    const label = money(min);
    if (!label) return '';
    const spread = p.price_max != null && String(p.price_max) !== String(min);
    return spread ? window.TD_SEARCH_STRINGS.from.replace('%price%', label) : label;
  }

  /* --- potrivirea nuantei cand API-ul nu o da ------------------------------------
     `/search/suggest.json` ataseaza varianta potrivita in `p.variants` doar cand un cuvant
     din interogare se potriveste INTREG ("charcoal", "jade wh" — ultimul cuvant partial dupa
     unul intreg). La un singur cuvant partial sau scris gresit ("charco", "charcho") gaseste
     produsul, dar intoarce `variants: []`, deci raspunsul era familia "PLA Matte", nu nuanta.
     Verificat pe API inainte de a scrie codul, inclusiv cu `resources[options][fields]`, care
     nu schimba nimic. Aici completam noi nuanta din `/products/<handle>.js`. */
  const PREFIX_MIN = 4;   // cate litere comune cerem ca sa acceptam o potrivire scrisa gresit

  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function words(value) {
    const n = norm(value);
    return n ? n.split(' ') : [];
  }

  function sharedPrefix(a, b) {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
    return i;
  }

  /* Un cuvant din interogare "atinge" un cuvant din date daca e prefixul lui (cautare in curs
     de tastare) sau daca impart destule litere la inceput (greseala de tastare). */
  function touches(token, word) {
    if (!token || !word) return false;
    if (word.startsWith(token) || token.startsWith(word)) return true;
    return sharedPrefix(token, word) >= PREFIX_MIN;
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.panel = this.querySelector('#td-search-results');
      this.form = this.querySelector('form');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.toggle = this.querySelector('[data-search-toggle]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.panel) return;
      this.productCache = new Map();

      this.limit = Number(this.dataset.limit) || 6;
      this.index = -1;
      this.items = [];
      this.timer = null;
      this.controller = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => { if (this.items.length) this.open(); });
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.reset(true));
      if (this.toggle) this.toggle.addEventListener('click', () => this.toggleField());

      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }

    toggleField() {
      const open = this.classList.toggle('is-open');
      this.toggle.setAttribute('aria-expanded', String(open));
      if (open) this.input.focus();
      else this.close();
    }

    onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      clearTimeout(this.timer);
      if (q.length < MIN_CHARS) { this.close(); this.items = []; return; }
      /* Debounce: fara el, fiecare tasta ar trimite o cerere, iar raspunsurile ar putea
         sosi in alta ordine decat au plecat si ar suprascrie rezultatul corect. */
      this.timer = setTimeout(() => this.search(q), DEBOUNCE);
    }

    async search(q) {
      if (this.controller) this.controller.abort();   // anulam cererea anterioara, inca in zbor
      this.controller = new AbortController();
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                  '&resources[type]=product&resources[limit]=' + this.limit +
                  '&resources[options][unavailable_products]=last';
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        await this.resolveShades(products, q);
        this.render(products, q);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }

    /* Completeaza nuanta pentru produsele carora API-ul nu le-a atasat una. Cere
       `/products/<handle>.js` doar cand interogarea contine un cuvant care NU e in titlul
       produsului — altfel potrivirea a fost pe titlu ("pla matte") si familia e raspunsul
       corect. Raspunsurile sunt tinute in memorie, deci o familie se cere o singura data. */
    async resolveShades(products, q) {
      const tokens = words(q).filter((t) => t.length >= 3);
      if (!tokens.length) return;

      const nevoie = products.filter((p) => {
        if ((p.variants || []).some((v) => v && v.title && v.title !== 'Default Title')) return false;
        const titlu = words(p.title).concat(words(p.type));
        return tokens.some((t) => !titlu.some((w) => touches(t, w)));
      }).slice(0, 4);   // marginim numarul de cereri suplimentare
      if (!nevoie.length) return;

      await Promise.all(nevoie.map(async (p) => {
        const handle = (String(p.url || '').match(/\/products\/([^/?#]+)/) || [])[1];
        if (!handle) return;
        try {
          if (!this.productCache.has(handle)) {
            const res = await fetch('/products/' + handle + '.js', { signal: this.controller.signal });
            this.productCache.set(handle, res.ok ? await res.json() : null);
          }
          const produs = this.productCache.get(handle);
          if (!produs || !produs.variants) return;

          /* Cea mai buna varianta = cea care acopera cele mai multe cuvinte din interogare
             negasite in titlu; la egalitate castiga una pe stoc. */
          let best = null;
          produs.variants.forEach((v) => {
            const valori = (v.options || []).concat([v.title]).flatMap(words);
            const scor = tokens.filter((t) => valori.some((w) => touches(t, w))).length;
            if (!scor) return;
            if (!best || scor > best.scor || (scor === best.scor && v.available && !best.v.available)) {
              best = { v: v, scor: scor };
            }
          });
          if (!best) return;

          /* `/products/<handle>.js` da pretul in bani, spre deosebire de suggest.json, care il
             da ca text in lei — impartim aici, ca `money()` sa primeasca acelasi format. */
          p.variants = [{
            title: best.v.title,
            url: '/products/' + handle + '?variant=' + best.v.id,
            price: (best.v.price / 100).toFixed(2),
          }];
        } catch (err) {
          if (err.name !== 'AbortError') this.productCache.set(handle, null);
        }
      }));
    }

    render(products, q) {
      this.index = -1;
      if (!products.length) {
        this.panel.innerHTML = '<p class="td-search__empty">' +
          window.TD_SEARCH_STRINGS.empty.replace('%terms%', esc(q)) + '</p>';
        this.items = [];
        this.open();
        this.announce(window.TD_SEARCH_STRINGS.none);
        return;
      }

      const rows = products.map((p, i) => {
        const img = p.featured_image && p.featured_image.url
          ? '<img src="' + esc(p.featured_image.url) + '&width=96" alt="" loading="lazy" width="48" height="48">'
          : '<span class="td-search__thumb--empty"></span>';

        /* Fiecare familie de filament e UN produs, iar nuantele sunt variante. Cand cautarea
           a potrivit o nuanta ("jade white"), API-ul intoarce varianta in `p.variants` —
           afisam nuanta, nu familia, altfel cumparatorul primea "PLA Basic" si trebuia sa
           caute culoarea din nou in pagina. */
        const v = (p.variants || []).find((x) => x && x.title && x.title !== 'Default Title');
        const primar = v ? v.title : p.title;
        const secundar = v ? p.title : (p.type || '');
        const href = v && v.url ? v.url : p.url;
        const price = v && v.price != null ? money(v.price) : priceLabel(p);

        return '<a class="td-search__row" id="td-search-row-' + i + '" role="option" aria-selected="false"' +
          ' href="' + esc(href) + '">' +
          '<span class="td-search__thumb">' + img + '</span>' +
          '<span class="td-search__meta"><b>' + esc(primar) + '</b>' +
          (secundar ? '<span>' + esc(secundar) + '</span>' : '') + '</span>' +
          (price ? '<span class="td-search__price">' + price + '</span>' : '') +
          '</a>';
      }).join('');

      const all = '<a class="td-search__all" href="/search?q=' + encodeURIComponent(q) + '&type=product">' +
        window.TD_SEARCH_STRINGS.all.replace('%terms%', esc(q)) + ' &rarr;</a>';

      this.panel.innerHTML = rows + all;
      this.items = Array.from(this.panel.querySelectorAll('.td-search__row, .td-search__all'));
      this.open();
      this.announce(window.TD_SEARCH_STRINGS.count.replace('%count%', products.length));
    }

    announce(text) { if (this.status) this.status.textContent = text; }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.index = -1;
    }

    reset(focus) {
      this.input.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      this.items = [];
      this.close();
      if (focus) this.input.focus();
    }

    onKey(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!this.items.length) return;
        e.preventDefault();
        this.index += e.key === 'ArrowDown' ? 1 : -1;
        if (this.index < 0) this.index = this.items.length - 1;
        if (this.index >= this.items.length) this.index = 0;
        this.items.forEach((el, i) => {
          const on = i === this.index;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-selected', String(on));
        });
        const active = this.items[this.index];
        if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'Enter' && this.index >= 0 && this.items[this.index]) {
        e.preventDefault();                       // altfel s-ar trimite formularul
        window.location.href = this.items[this.index].href;
      }
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
